import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "./DashboardHeader";
import MetricsGrid from "./MetricsGrid";
import BrandStatsCard from "./BrandStatsCard";
import FeedbackAndWaitlist from "./FeedbackAndWaitlist";
import ViewAllBrandsButton from "./ViewAllBrandsButton";
import BrandStatsCardSkeleton from "./Skeletons/BrandStatsCardSkeleton";
import CardsSkeleton from "./Skeletons/CardsSkeleton";
import FeedbackAndWaitlistSkeleton from "./Skeletons/FeedbackAndWaitlistSkeleton";

import {
  fetchInsights,
  dataCollectionInsights,
  fetchTotalTokens,
  getUsersSignupsTotal,
  getUsersSignupsInDuration,
} from "../../services/dataAnalyticsApi";
import ThemesAndUsers from "./ThemesAndUsers";
import QuestionType from "./QuestionType";
import IngestedDataInsights from "./IngestedDataInsights";

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const firstDayOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1
  );
  const today = new Date();

  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(today);

  const formatDateForApi = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getDataInsights = async (start, end) => {
    if (!start || !end) {
      setError("Please select both start and end dates.");
      return;
    }
    setError("");
    try {
      setLoading(true);
      const formattedStart = formatDateForApi(new Date(start));
      const formattedEnd = formatDateForApi(new Date(end));
      const beginningDate = "2025-03-21";
      const endDate = formatDateForApi(today);
      localStorage.setItem("startDate", formattedStart);
      localStorage.setItem("endDate", formattedEnd);
      const [
        insightsRes,
        totalInsightsRes,
        dataCollectionRes,
        tokensInDuration,
        totalTokensRes,
        usersInDurationRes,
        totalSignupRes,
      ] = await Promise.all([
        fetchInsights(formattedStart, formattedEnd),
        fetchInsights(beginningDate, endDate),
        dataCollectionInsights(formattedStart, formattedEnd),
        fetchTotalTokens(formattedStart, formattedEnd),
        fetchTotalTokens(beginningDate, endDate),
        getUsersSignupsInDuration(formattedStart, formattedEnd),
        getUsersSignupsTotal(),
      ]);

      if (insightsRes.data.status && dataCollectionRes.data.success) {
        setInsights({
          summary: totalSignupRes.data.summary,
          usersInDuration: usersInDurationRes.data.summary,
          dataCollection: dataCollectionRes.data.data,
          tokensInDuration: tokensInDuration.data,
          tokenCount: totalTokensRes.data,
          waitlist: insightsRes.data.waitlist,
          insightsInDuration: insightsRes.data.summary,
          totalInsights: totalInsightsRes.data.summary,
          users: usersInDurationRes.data.users,
          questionType: insightsRes.data.question_type_insights
        });
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      setError("Unable to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getDataInsights(startDate, endDate);
  }, []);

  const handleDateRangeChange = ({ startDate: newStart, endDate: newEnd }) => {
    setStartDate(newStart);
    setEndDate(newEnd);
    getDataInsights(newStart, newEnd);
  };
  const isToday = (date) => {
    if (!date) return false; 
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };
  const showDurationIcon = !(isToday(startDate) && isToday(endDate));

  return (
    <div className="dashboard-wrapper">
      <ViewAllBrandsButton />
      <DashboardHeader
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateRangeChange}
      />
      {error && <div style={{ color: "red", margin: "10px 0" }}>{error}</div>}
      {loading ? (
        <CardsSkeleton />
      ) : (
        <MetricsGrid
          insights={{
            summary: insights?.summary,
            dataCollection: insights?.dataCollection,
            usersInDuration: insights?.usersInDuration,
            tokensInDuration: insights?.tokensInDuration,
            tokenCount: insights?.tokenCount,
            insightsInDuration: insights?.insightsInDuration,
            totalInsights: insights?.totalInsights,
          }}
          showDurationIcon={showDurationIcon}
        />
      )}
      {/* Ingested Data Insights Section */}
      <IngestedDataInsights startDate={startDate}
       endDate={endDate} />

      {/* RAG Search Section */}

      {loading ? (
        <BrandStatsCardSkeleton />
      ) : (
        <BrandStatsCard
          insights={{
            brandData: insights?.insightsInDuration?.brand_data,
            totalQuestions: insights?.summary?.total_questions,
          }}
        />
      )}

      {/* Section Divider */}
      <div
        style={{
          borderBottom: "2px solid #e9ecef",
          margin: "40px 0",
          width: "100%",
        }}
      />

      <ThemesAndUsers
        startDate={startDate}
        endDate={endDate}
        data={{
          mostAskedThemes:
            insights?.insightsInDuration?.brand_data.most_asked_theme,
          users: insights?.users,
        }}
      />
      {loading ? (
        <FeedbackAndWaitlistSkeleton />
      ) : (
        <FeedbackAndWaitlist
          insights={{
            feedBackCount: insights?.insightsInDuration?.feedback_count,
            totalQuestions: insights?.insightsInDuration?.total_questions,
            waitList: insights?.waitlist,
          }}
        />
      )}
      <QuestionType insights={insights?.questionType} />
    </div>
  );
};
export default AnalyticsDashboard;
